<script setup lang="ts">
/**
 * The panel: a header, a body that depends on the phase, and — once there is a
 * conversation — a composer.
 *
 * `role="dialog"` with `aria-modal="false"`. It IS a dialog: a named region that
 * opens over the page and can be dismissed. It is NOT modal, and claiming
 * otherwise would cost the visitor something real — a screen reader told
 * `aria-modal="true"` hides the rest of the document from its user, and the rest
 * of the document here is the customer's website, which the visitor is in the
 * middle of using.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import type { WidgetView } from '../app/useWidget';
import { staffedLocales } from '../i18n/locale';
import { WIDGET_LOCALE_ENDONYMS } from '../i18n/locales';
import { logoUrl } from '../theme/appearance';
import { useText } from '../vendor/i18n';
import { OwButton, OwIconButton, OwSpinner } from '../vendor/ui';

import ClosedNotice from './ClosedNotice.vue';
import Composer from './Composer.vue';
import ConsentNotice from './ConsentNotice.vue';
import History from './History.vue';
import PastConversation from './PastConversation.vue';
import PrechatForm from './PrechatForm.vue';
import Transcript from './Transcript.vue';
import UnavailableNotice from './UnavailableNotice.vue';

const props = defineProps<{
  readonly view: WidgetView;
  readonly open: boolean;
}>();

const emit = defineEmits<{
  requestClose: [];
  label: [text: string];
}>();

const text = useText();

/**
 * Whether the "end this chat?" bar is showing.
 *
 * LOCAL TO THE PANEL, not on the view. Nothing outside this component acts on
 * it: it is not persisted, it is not a request, and a visitor who minimises the
 * panel mid-question has answered it by leaving. Putting it in `useWidget` would
 * make a rendering state look like part of the conversation's.
 */
const confirmingEnd = ref(false);
const chat = computed(() => props.view.screen.kind === 'chat');

/**
 * The launcher's accessible name lives on the HOST page, so the copy has to
 * travel up. Two variants rather than a count, because "2 нових повідомлення"
 * needs Ukrainian's plural forms and this app deliberately ships no ICU parser
 * — the digit itself is drawn by the loader as a badge beside the name.
 *
 * A computed, so it re-sends when the CATALOGUE changes too: `text` reads it at
 * call time, and the English first frame is corrected the moment the visitor's
 * language arrives.
 */
const launcherLabel = computed(() =>
  props.view.unread > 0 ? text('widget.launcher_unread') : text('widget.launcher'),
);
watch(
  launcherLabel,
  (label) => {
    emit('label', label);
  },
  { immediate: true },
);

const onKeyDown = (event: KeyboardEvent): void => {
  if (event.key !== 'Escape') {
    return;
  }
  // Escape is handled HERE and not by the loader: inside the frame it is
  // unambiguously ours, while on the host page it might have been meant for
  // their menu or their lightbox. A widget that eats a keystroke on someone
  // else's site is the kind of bad manners that gets it removed.
  //
  // ONE STEP AT A TIME (P2-52): on the history or past screen it goes BACK
  // rather than minimising. Escape means "undo the thing I just opened", and a
  // visitor two screens deep who is thrown out to the host page has to find the
  // launcher and navigate in again to undo one tap.
  if (chat.value) {
    emit('requestClose');
  } else {
    props.view.backToChat();
  }
};
window.addEventListener('keydown', onKeyDown);
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown);
});

// The confirm bar belongs to the chat screen and to the moment it was opened
// in; it must not still be waiting for an answer behind a history list.
watch(chat, (isChat) => {
  if (!isChat) {
    confirmingEnd.value = false;
  }
});

/**
 * The title is this product's word for "chat", not the brand's name.
 * `WidgetAppearance` carries no brand name — deliberately, per the contract:
 * what the iframe receives is what it needs to draw a chat window and nothing
 * that could later be sent back as an authorisation input. The brand's identity
 * on screen is its logo and its accent colour.
 */
const logo = computed(() => logoUrl(props.view.appearance));
const other = computed(() =>
  staffedLocales(props.view.appearance?.locales ?? []).find(
    (locale) => locale !== props.view.locale,
  ),
);

/**
 * ON THE HISTORY SCREENS THE PANEL IS CALLED "PAST CONVERSATIONS" (P2-52).
 *
 * The dialog's accessible name is what a screen reader announces when focus
 * enters it, so leaving it as "Chat" on a list of old conversations would
 * describe the wrong screen. The `past` screen keeps the same title rather than
 * inventing one from the conversation's date: a date is not a name, it would be
 * the only untranslated string in the header, and the visitor arrived here from
 * a row they just read the date on.
 */
const title = computed(() => (chat.value ? text('widget.title') : text('widget.history')));

const closed = computed(
  () => props.view.conversation !== null && props.view.conversation.statusCategory === 'closed',
);
/** Ending is offered only on a live chat the visitor has not already left. */
const canEnd = computed(
  () => chat.value && props.view.conversation !== null && !closed.value && !props.view.fresh,
);

/*
 * THE PHASE DECIDES FIRST, THEN THE SCREEN. A widget that lost the API while the
 * visitor was reading their history has to say so — rendering a stale list
 * under a header that still offers to page it is worse than one honest notice.
 * The two screens below are reachable only from `ready`, so this ordering costs
 * nothing in the normal case.
 */
const body = computed(() => {
  const phase = props.view.phase.kind;
  if (phase === 'ready' && props.view.screen.kind === 'history') return 'history';
  if (phase === 'ready' && props.view.screen.kind === 'past') return 'past';
  return phase;
});

/**
 * A CLOSED CONVERSATION HAS NO COMPOSER — P2-52, ADR-0142.
 *
 * `closed` is terminal for the visitor: the server appends only to a
 * conversation that is not closed, so a composer here would take a message and
 * open a NEW conversation with it, silently, while the visitor was looking at
 * the old one. The notice replaces it and says what happened, with the one
 * control that does what they meant.
 *
 * `fresh` is the same screen after they took it: the transcript the composable
 * hands over is empty, the composer is back, and the conversation underneath is
 * still the closed one until the send creates its replacement.
 */
const ended = computed(() => closed.value && !props.view.fresh);
</script>

<template>
  <div class="ow-panel" role="dialog" aria-modal="false" :aria-label="title">
    <header class="ow-header">
      <OwIconButton
        v-if="!chat"
        :label="text('widget.back')"
        :title="text('widget.back')"
        class="ow-header__icon"
        @click="view.backToChat()"
      >
        <!-- A left arrow, mirrored with the panel: `scaleX(-1)` under `dir=rtl`
             is what keeps "back" pointing at the edge the visitor came from in
             the four right-to-left languages this app ships. -->
        <svg
          class="ow-icon--directional"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          width="18"
          height="18"
        >
          <path fill="currentColor" d="M15.4 4 7 12.4l8.4 8.4 1.4-1.4-7-7 7-7z" />
        </svg>
      </OwIconButton>
      <!-- Empty alt: the heading beside it already names the panel, so a
           screen reader announcing the logo as well would just repeat itself. -->
      <img
        v-if="logo !== null && chat"
        class="ow-header__logo"
        :src="logo"
        alt=""
        width="24"
        height="24"
      />
      <h1 class="ow-header__title">{{ title }}</h1>
      <div class="ow-header__actions">
        <OwIconButton
          v-if="canEnd"
          :label="text('widget.end_chat')"
          :title="text('widget.end_chat')"
          class="ow-header__icon"
          @click="confirmingEnd = true"
        >
          <!-- A door with an arrow leaving it — "end this", not "close the
               panel", which is what the × beside it already means. -->
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="18" height="18">
            <path
              fill="currentColor"
              d="M5 3h7v2H7v14h5v2H5zm9.6 3.6 4.9 4.9a.7.7 0 0 1 0 1l-4.9 4.9-1.4-1.4 3-3H9.5v-2h6.7l-3-3z"
            />
          </svg>
        </OwIconButton>
        <OwIconButton
          v-if="view.phase.kind === 'ready' && chat"
          :label="text('widget.history')"
          :title="text('widget.history')"
          class="ow-header__icon"
          @click="view.openHistory()"
        >
          <!-- A clock rewinding: the history is "what I wrote before", and a
               list icon would read as a menu on a panel that has no menu. -->
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="18" height="18">
            <path
              fill="currentColor"
              d="M12 3a9 9 0 1 0 8.5 12H18.3A7 7 0 1 1 12 5a6.9 6.9 0 0 1 5 2.1L14 10h7V3l-2.6 2.6A8.9 8.9 0 0 0 12 3zm-1 4v5.4l4.3 2.6.8-1.3-3.6-2.2V7z"
            />
          </svg>
        </OwIconButton>
        <!-- The visible label is an endonym ("EN", "Укр") that names the language
             it switches TO but not what the control does; the tooltip says
             that. It is `title` and NOT `aria-label`, because the endonym is
             already the accessible name. -->
        <OwButton
          v-if="other !== undefined && chat"
          size="sm"
          variant="ghost"
          class="ow-header__language"
          :title="text('widget.language')"
          @click="view.setLocale(other)"
        >
          {{ WIDGET_LOCALE_ENDONYMS[other] }}
        </OwButton>
        <OwIconButton
          :label="text('widget.close')"
          :title="text('widget.close')"
          class="ow-header__close"
          @click="emit('requestClose')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="18" height="18">
            <path
              fill="currentColor"
              d="M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12 19 6.4 17.6 5 12 10.6z"
            />
          </svg>
        </OwIconButton>
      </div>
    </header>

    <div class="ow-body">
      <!--
        THE CONFIRM IS INLINE AND AT THE TOP OF THE BODY, not a modal.

        A modal would have to trap focus, and this panel is `aria-modal="false"`
        on purpose — it floats over the customer's website, which the visitor is
        in the middle of using. A bar above the transcript asks the question
        without taking the page hostage, and leaves the conversation they are
        deciding about visible behind it.

        `role="group"` rather than `alertdialog`: nothing is being interrupted
        and no focus is being seized. The `aria-label` is the question itself,
        so the group announces what it is asking.
      -->
      <div
        v-if="confirmingEnd && canEnd"
        class="ow-confirm"
        role="group"
        :aria-label="text('widget.end_chat.confirm')"
      >
        <p class="ow-confirm__text">{{ text('widget.end_chat.confirm') }}</p>
        <div class="ow-confirm__actions">
          <OwButton
            size="sm"
            variant="danger"
            :disabled="view.closing"
            :busy="view.closing"
            :busy-label="text('widget.end_chat')"
            @click="view.endChat()"
          >
            {{ text('widget.end_chat.yes') }}
          </OwButton>
          <OwButton size="sm" variant="ghost" @click="confirmingEnd = false">
            {{ text('widget.end_chat.cancel') }}
          </OwButton>
        </div>
      </div>

      <History v-if="body === 'history'" :view="view" />
      <PastConversation v-else-if="body === 'past'" :view="view" />

      <div v-else-if="body === 'booting'" class="ow-centred">
        <OwSpinner :label="text('widget.conversation.loading')" />
      </div>

      <!-- No retry button. The session route answers 404 for an unknown key, a
           revoked key and an origin outside the allowlist alike — a decision,
           not a hiccup. Offering "try again" for a decision would be a lie. -->
      <UnavailableNotice
        v-else-if="body === 'unavailable'"
        :detail="text('widget.error.unavailable')"
      />

      <UnavailableNotice v-else-if="body === 'offline'" :detail="text('widget.error.unreachable')">
        <OwButton variant="secondary" @click="view.reconnect()">
          {{ text('widget.error.retry') }}
        </OwButton>
      </UnavailableNotice>

      <PrechatForm
        v-else-if="body === 'prechat'"
        :fields="view.prechatFields"
        :submitting="view.submitting"
        @submit="view.submitPrechat"
      />

      <template v-else>
        <Transcript :messages="view.messages" :pending="view.pending" :retry="view.retry" />
        <!-- BETWEEN the transcript and the composer, and only in `ready`.
             A consent step above the transcript would sit between the visitor
             and the reply they are waiting for; one in `prechat` would be a
             second form before the first one; one in `booting` or `offline`
             would ask for a decision we could not act on. Here it is beside the
             thing it is about — the agent is about to ask "which page are you
             on?" — and it renders nothing at all when there is nothing to
             decide, which today is always. See `ConsentNotice`'s header. -->
        <ConsentNotice
          :consent="view.consent"
          :available="view.consentPurposes"
          @grant="view.grantConsent"
          @refuse="view.refuseConsent"
        />
        <ClosedNotice v-if="ended" closed :start-new="view.startNewConversation" />
        <Composer v-else :auto-focus="open" @send="view.send" />
      </template>
    </div>
  </div>
</template>
